/**
 * Персональные данные не уходят в Sentry: проект Sentry в ЕС, так что любая почта или имя в
 * событии — это ещё и трансграничная передача (152-ФЗ). Текст и картинки в Session Replay
 * маскирует сам SDK (см. main.tsx); здесь — то, что SDK пропускает: строки в событиях, крошках,
 * спанах и служебных событиях реплея. Политика целиком — docs/sentry-privacy.md.
 */

const FILTERED = '[Filtered]';

/**
 * Почта, в том числе URL-кодированная (`user%40mail.ru` в query). Длины частей ограничены, чтобы
 * длинная строка без разделителей (base64, data-URL) не давала квадратичный перебор.
 */
const EMAIL_RE = /[A-Z0-9._%+-]{1,64}(?:@|%40)[A-Z0-9.-]{1,253}\.[A-Z]{2,24}/gi;

/**
 * Параметры query, значения которых не отправляем: почта и одноразовый токен из писем
 * (`/verify-email?token=…&email=…`), поиск — в админке ищут по почте и имени, на карте — по
 * содержимому объектов. `clientId` — ключ гостя на правку и удаление своих публичных
 * комментариев: теперь он идёт заголовком, но старые сборки клиента ещё шлют его в адресе.
 */
const SENSITIVE_QUERY_PARAM_RE = /([?&](?:email|token|code|search|q|query|name|clientId)=)[^&#\s"'<>]*/gi;

/**
 * Токен в ПУТИ ссылки из письма: сброс пароля (`/reset-password/:token`) и приглашение в команду
 * (`/invites/workspace/:token`, ручка `…/:token/accept`). Путь страницы SDK пишет в имя и
 * атрибуты корневого спана и в крошки навигации, а действующий токен сброса — это вход в чужой
 * аккаунт. Разделитель — `/` или `%2F`: путь приглашения едет и в `?next=` после входа.
 */
const SENSITIVE_PATH_TOKEN_RE = /((?:\/|%2F)(?:reset-password|invites(?:\/|%2F)workspace)(?:\/|%2F))[^/?#&\s"'<>%]+/gi;

/**
 * Атрибуты в CSS-селекторах крошек `ui.click` (`span.truncate[title="…"]`): SDK берёт их прямо из
 * DOM, мимо маскировки реплея, а в `title` у нас бывает почта участника.
 */
const SELECTOR_TEXT_ATTR_RE = /\[(title|aria-label|alt)="[^"]*"\]/g;

/**
 * Защита стека от аномально глубоких данных. SDK нормализует события до нескольких уровней, так
 * что до предела обычные данные не доходят; дальше — маркер, а не сырое поддерево.
 */
const MAX_DEPTH = 100;

export function scrubPiiString(value: string): string {
  let out = value;
  if (out.includes('@') || out.includes('%40')) {
    out = out.replace(EMAIL_RE, '[email]');
  }
  if (out.includes('=')) {
    out = out
      .replace(SENSITIVE_QUERY_PARAM_RE, `$1${FILTERED}`)
      .replace(SELECTOR_TEXT_ATTR_RE, `[$1="${FILTERED}"]`);
  }
  if (/reset-password|invites/i.test(out)) {
    out = out.replace(SENSITIVE_PATH_TOKEN_RE, `$1${FILTERED}`);
  }
  return out;
}

/**
 * Копия значения, в которой все строки прошли `scrubPiiString`. Обходит массивы и простые объекты
 * (событие Sentry к `beforeSend` уже нормализовано в JSON); экземпляры классов и
 * `sdkProcessingMetadata` оставляет по ссылке. Повторная ссылка и цикл ведут на ту же очищенную копию.
 */
export function scrubPiiDeep<T>(value: T): T {
  return walk(value, 0, new WeakMap()) as T;
}

function walk(value: unknown, depth: number, copies: WeakMap<object, unknown>): unknown {
  if (typeof value === 'string') {
    return scrubPiiString(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (copies.has(value)) {
    return copies.get(value);
  }
  if (depth > MAX_DEPTH) {
    return '[Truncated]';
  }
  if (Array.isArray(value)) {
    const arr: unknown[] = [];
    // Копию регистрируем до обхода детей: цикл замкнётся на неё, а не на сырой оригинал.
    copies.set(value, arr);
    for (const item of value) {
      arr.push(walk(item, depth + 1, copies));
    }
    return arr;
  }
  const proto = Object.getPrototypeOf(value) as unknown;
  if (proto !== Object.prototype && proto !== null) {
    return value;
  }
  const out: Record<string, unknown> = {};
  copies.set(value, out);
  for (const [key, item] of Object.entries(value)) {
    // Служебные ссылки SDK на scope и спаны: в Sentry не уходят, копировать их нельзя.
    out[key] = key === 'sdkProcessingMetadata' ? item : walk(item, depth + 1, copies);
  }
  return out;
}

/**
 * Страница открыта по ссылке из письма: почта или токен в адресе (в query или в пути, как у
 * сброса пароля). rrweb кладёт `location.href` в meta-событие записи, а
 * `beforeAddRecordingEvent` его не видит — реплей в такой сессии не включаем.
 */
export function hasPiiInUrl(url: string): boolean {
  return scrubPiiString(url) !== url;
}

/**
 * Какие события поднимают буфер реплея. Только ошибки: warning/info из `captureMessage`
 * (перф-сигналы вроде «syncPreviewPointLayer slow») не повод выгружать запись сессии.
 */
export function shouldSampleReplayForEvent(event: { level?: string }): boolean {
  return event.level === undefined || event.level === 'error' || event.level === 'fatal';
}
